import React, { Fragment, useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { Dropdown as RsuiteDropdown } from "rsuite";
import "../../styles/NavBar.css";
import "../../styles/Stats.css";
import "../../styles/Tabs.css";
import { AppContext } from "../../provider/AppContext";
import AAvatar from "../../atoms/MUI/Avatar";
import { DarkMode } from "../../styles/ThemePallette";
import "react-color-palette/lib/css/styles.css";
import NotificationComponent, {
  NotificationBadge,
  UserBadge,
} from "../Notifications/Notifications";
import { ActiveUsers } from "../ActiveUsers/ActiveUsers";
import HamburgerMenu from "./HamburgerMenu";
import ContractForm from "../NewContractForm/ContractForm";
import { accessTypes } from "../../config/accessTypes";
import { hasAccess } from "../../utils/accessControl";
import { useOpenContractTab } from "../../hooks/useOpenContractTab";
import DescriptionIcon from "@mui/icons-material/Description";
import BarChartIcon from "@mui/icons-material/BarChart";
import PeopleIcon from "@mui/icons-material/People";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TableChartIcon from "@mui/icons-material/TableChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RepeatIcon from "@mui/icons-material/Repeat";

export const executiveRoles = [
  "Admin",
  "Owner",
  "Event Director Lead",
  "Event Director Lead Auditor",
];

const Navbar = (props) => {
  const {
    notificationCount,
    getNotifications,
    activeUsersSummary,
    handleReadNotification,
    role,
  } = useContext(AppContext);
  const [showNotifications, setNotifications] = useState(false);
  const [activeModal, setActiveModal] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const location = useLocation();

  const handleClick = useOpenContractTab();

  const hasAccessClientProfile = hasAccess(
    role,
    accessTypes.statistics.canViewClientProfile
  );
  const canEditContract = hasAccess(
    role,
    accessTypes.contractEditing.canEditContract
  );

  const isPathActive = (path) => {
    if (path === "/contracts") {
      return location.pathname === "/contracts" || location.pathname.startsWith("/contracts");
    }
    if (path === "/customers") {
      return location.pathname === "/customers" || location.pathname.startsWith("/customers");
    }
    if (path === "/vendors") {
      return location.pathname === "/vendors" || location.pathname.startsWith("/vendors");
    }
    return location.pathname === path;
  };

  const navItem = (
    path,
    label,
    className = "nav-link btn",
    index,
    onClick = null
  ) => {
    const isActive = isPathActive(path);
    const activeClass = isActive ? `${className}-active` : "";

    return (
      <li className="nav-item" key={index}>
        {!!props.isAuthenticated && (
          <Link
            className={`nav-link btn !tw-text-sm tw-py-2 ${className} ${activeClass}`}
            to={onClick ? "#" : path}
            onClick={onClick}
          >
            {label}
          </Link>
        )}
      </li>
    );
  };

  const renderDropdownItems = (items, includeLogout = false) => (
    <>
      {items.map((item, index) => (
        <Fragment key={index}>
          <Link className="dropdown-link" to={item.path}>
            {item.label}
          </Link>
        </Fragment>
      ))}
      {includeLogout && (
        <>
          <div className="dropdown-divider" />
          <span
            className="dropdown-link dropdown-link-danger"
            onClick={(e) => {
              e.preventDefault();
              props.logout(e);
            }}
            style={{ cursor: "pointer" }}
          >
            Logout
          </span>
        </>
      )}
    </>
  );

  const dropdownForRole = (items) =>
      <Dropdown>
        <Dropdown.Toggle className="owner-dropdown">
          <AAvatar />
          <span className="tw-pl-1.5">Settings</span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <div className="d-flex flex-column">{renderDropdownItems(items, true)}</div>
        </Dropdown.Menu>
      </Dropdown>

  const statsDropdownItems = [
    { path: "/stats/table", label: "Table View", icon: <TableChartIcon sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} /> },
    { path: "/stats/graph", label: "Graph View", icon: <ShowChartIcon sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} /> },
    { path: "/stats/expense", label: "Expense Report", icon: <AccountBalanceWalletIcon sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} /> },
    { path: "/stats/progress", label: "Progress Report", icon: <AssessmentIcon sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} /> },
  ];

  if (props.isAdmin) {
    statsDropdownItems.push({ path: "/recurring-report", label: "Recurring Report", icon: <RepeatIcon sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} /> });
  }

  const isStatsActive = location.pathname.startsWith("/stats") || location.pathname === "/recurring-report";

  const renderStatsButton = (buttonProps, ref) => (
    <li className="nav-item" key="stats">
      <span
        {...buttonProps}
        ref={ref}
        className={`nav-link btn !tw-text-sm tw-py-2 nav-item-stats ${isStatsActive ? "nav-item-stats-active" : ""}`}
      >
        <BarChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Stats
      </span>
    </li>
  );

  const adminItems = [
    { path: "/pricing/employee", label: "Pricing +/-" },
    { path: "/password", label: "Change Password" },
    { path: "/profile", label: "Edit Profile" },
    { path: "/createemployee", label: "Add Employee" },
    { path: "/employees", label: "Edit Employee Roles" },
    { path: "/access-control", label: "Access Control" },
    { path: "/restore-contract", label: "Restore Contract" },
    { path: "/client", label: "Client Profiles" },
    { path: "/templates", label: "Contract Templates" },
    { path: "/reports", label: "Reports Table" },
    
  ];

  const userItems = [
    { path: "/client", label: "Client Profiles +/-" },
    { path: "/password", label: "Change Password" },
    { path: "/profile", label: "Edit Profile" },
    { path: "/restore-contract", label: "Restore Contract" },
  ];

  return (
    <Fragment>
      {!!props.isAuthenticated && (
        <div>
          <ContractForm
            showForm={showContractForm}
            setShowForm={setShowContractForm}
            handleClick={handleClick}
          />
          <HamburgerMenu
            showNotifications={showNotifications}
            setNotifications={setNotifications}
            isAdmin={props.isAdmin}
            adminItems={adminItems}
            userItems={userItems}
            dropdownForRole={dropdownForRole}
            navItem={navItem}
            notificationCount={notificationCount}
            getNotifications={getNotifications}
            handleReadNotification={handleReadNotification}
            activeUsersSummary={activeUsersSummary}
            setActiveModal={setActiveModal}
            hasAccessClientProfile={hasAccessClientProfile}
            canEditContract={canEditContract}
            setShowContractForm={setShowContractForm}
          />

          <ActiveUsers {...{ activeModal, setActiveModal }} />
          <NotificationComponent {...{ showNotifications, setNotifications }} />
          <nav
            style={{ borderBottom: "1px solid black" }}
            className="navbar navbar-expand-lg navbar-dark"
          >
            <div className="container-fluid">
              <Link to="/home">
                <div className="image-wrapper">
                  <img
                    className="logo-image w-10"
                    height={10}
                    src="../images/headerlogo.png"
                    alt="TPE Logo"
                  />
                </div>
              </Link>
              <ul className="navbar-nav me-auto">
                {navItem(`/contracts`, <><DescriptionIcon sx={{ fontSize: 18, mr: 0.5 }} /> Contracts</>, "nav-item-contracts")}

                {/* Stats Dropdown */}
                <li className="nav-item">
                  <RsuiteDropdown
                    renderToggle={renderStatsButton}
                    placement="bottomStart"
                  >
                    {statsDropdownItems.map((item, index) => (
                      <RsuiteDropdown.Item
                        key={index}
                        as={Link}
                        to={item.path}
                        className={location.pathname === item.path ? "stats-item-active" : ""}
                      >
                        {item.icon}{item.label}
                      </RsuiteDropdown.Item>
                    ))}
                  </RsuiteDropdown>
                </li>

                {/* Customers */}
                {hasAccessClientProfile && navItem(`/customers`, <><PeopleIcon sx={{ fontSize: 18, mr: 0.5 }} /> Customers</>, "nav-item-customers")}

                {/* Vendors - placeholder */}
                {navItem(`/vendors`, <><StorefrontIcon sx={{ fontSize: 18, mr: 0.5 }} /> Vendors</>, "nav-item-vendors")}

                {/* Add Event */}
                {canEditContract && (
                  <li className="nav-item">
                    <span
                      className="nav-link btn !tw-text-sm tw-py-2 nav-item-add-event"
                      onClick={() => setShowContractForm(1)}
                      style={{ cursor: "pointer" }}
                    >
                      <AddCircleIcon sx={{ fontSize: 18, mr: 0.5 }} /> Add Event
                    </span>
                  </li>
                )}
              </ul>

              <div className="navbar-collapse" id="navbarColor02">
                <ul className="navbar-nav me-auto"></ul>
                <div className="nav-right-panel">
                  <div className="nav-icon-group">
                    <NotificationBadge
                      {...{
                        getNotifications,
                        handleReadNotification,
                        setNotifications,
                        notificationCount,
                      }}
                    />
                    <UserBadge {...{ activeUsersSummary, setActiveModal }} />
                    <div className="nav-icon-btn">
                      <DarkMode />
                    </div>
                  </div>
                  <div className="nav-settings-wrapper">
                    {props.isAdmin
                      ? dropdownForRole(adminItems)
                      : dropdownForRole(userItems)}
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </div>
      )}
    </Fragment>
  );
};

export default Navbar;
